import React, { useState, useEffect } from 'react';
import { 
  Users, 
  FileText, 
  Clock, 
  Calendar, 
  Plus,
  Eye,
  Download,
  ArrowRight,
  Phone, 
  Mail, 
  MessageSquare,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  Globe,
  TrendingUp,
  Activity,
  Target,
  Zap,
  Search,
  Filter,
  RefreshCw,
  BarChart3,
  CalendarDays,
  UserCheck,
  Building2
} from 'lucide-react';
import { getModeratorContacts, getAllApplications, generateWeeklyReport } from '../../services/moderatorService';
import { useAuthStore } from '../../stores/authStore';
import { JobApplication, CompanyContact } from '../../types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import AddContactModal from './AddContactModal';
import EditContactModal from './EditContactModal';

const ModeratorDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [contacts, setContacts] = useState<CompanyContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editContact, setEditContact] = useState<CompanyContact | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Carica contatti e applications in parallelo
      const [moderatorContacts, allApplications] = await Promise.all([
        getModeratorContacts(user?.uid || ''),
        getAllApplications()
      ]);
      
      setContacts(moderatorContacts);
      setApplications(allApplications);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
    setIsRefreshing(false);
  };

  const handleGenerateReport = async () => {
    if (!user) return;
    
    try {
      setGeneratingReport(true);
      await generateWeeklyReport(user.uid);
      alert('Report settimanale generato con successo!');
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Errore nella generazione del report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const closeDetailModal = () => {
    setSelectedApplication(null);
  };

  const closeContactModal = () => {
    setShowContactModal(false);
    setSelectedApplication(null);
  };

  const openEditModal = (contact: CompanyContact) => {
    setEditContact(contact);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setEditContact(null);
    setShowEditModal(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'In attesa': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'Colloquio programmato': return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'Accettato': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'Rifiutato': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getContactMethodIcon = (method: string) => {
    switch (method) {
      case 'Email': return <Mail className="w-4 h-4" />;
      case 'Telefono': return <Phone className="w-4 h-4" />;
      case 'LinkedIn': return <MessageSquare className="w-4 h-4" />;
      case 'Lettera': return <FileText className="w-4 h-4" />;
      case 'Form web': return <Globe className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  // Funzione per ottenere il nome del candidato da un contatto
  const getCandidateInfo = (contact: CompanyContact) => {
    const application = applications.find(app => app.id === contact.applicationId);
    return {
      name: application?.userName || 'N/A',
      email: application?.userEmail || 'N/A'
    };
  };

  // Calcola statistiche
  const stats = {
    totalApplications: applications.length,
    totalContacts: contacts.length,
    pendingApplications: applications.filter(app => app.status === 'In attesa').length,
    interviews: applications.filter(app => app.status === 'Colloquio programmato').length,
    positiveResponses: contacts.filter(c => c.response === 'positive').length,
    negativeResponses: contacts.filter(c => c.response === 'negative').length,
    pendingResponses: contacts.filter(c => c.response === 'pending').length
  };

  // Ordina le candidature per data (più recenti prima) e prendi solo le ultime 5
  const sortedApplications = applications.sort((a, b) => {
    if (!a.appliedDate || !b.appliedDate) return 0;
    
    const dateA = a.appliedDate?.toMillis ? a.appliedDate.toMillis() : (typeof a.appliedDate === 'number' ? a.appliedDate : 0);
    const dateB = b.appliedDate?.toMillis ? b.appliedDate.toMillis() : (typeof b.appliedDate === 'number' ? b.appliedDate : 0);
    
    return dateB - dateA;
  });
  
  const recentApplications = sortedApplications.slice(0, 5);
  const recentContacts = contacts.slice(0, 5);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Caricamento Dashboard...</h3>
          <p className="text-gray-600">Stiamo preparando i tuoi dati</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm w-full">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Errore</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={loadData}
            className="w-full bg-blue-600 text-white rounded-xl py-3 px-4 font-medium hover:bg-blue-700 transition-colors"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-4 lg:space-y-6">
      {/* Header con Welcome e Azioni Rapide */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 lg:gap-3 mb-2">
              <div className="p-1.5 lg:p-2 bg-blue-100 rounded-lg lg:rounded-xl">
                <Activity className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900">Dashboard Moderatore</h1>
                <p className="text-sm lg:text-base text-gray-600">Benvenuto! Ecco un riepilogo delle tue attività</p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 lg:gap-3">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-gray-100 text-gray-700 rounded-lg lg:rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm lg:text-base"
            >
              <RefreshCw className={`w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
            <button
              onClick={handleGenerateReport}
              disabled={generatingReport}
              className="flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-blue-600 text-white rounded-lg lg:rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm lg:text-base"
            >
              <Download className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
              {generatingReport ? 'Generando...' : 'Report'}
            </button>
            <button
              onClick={() => navigate('/moderator/report')}
              className="flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-purple-600 text-white rounded-lg lg:rounded-xl hover:bg-purple-700 transition-colors text-sm lg:text-base"
            >
              <BarChart3 className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
              Avanzato
            </button>
          </div>
        </div>
      </div>

      {/* Statistiche Principali - Mobile: 2x2, Desktop: 1x4 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Candidature Totali</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.totalApplications}</p>
              <p className="text-xs text-gray-500 mt-1">Gestite dal sistema</p>
            </div>
            <div className="p-2 lg:p-3 bg-blue-100 rounded-lg lg:rounded-xl">
              <Users className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Contatti Effettuati</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.totalContacts}</p>
              <p className="text-xs text-gray-500 mt-1">Con le aziende</p>
            </div>
            <div className="p-2 lg:p-3 bg-green-100 rounded-lg lg:rounded-xl">
              <FileText className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">In Attesa</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.pendingApplications}</p>
              <p className="text-xs text-gray-500 mt-1">Da seguire</p>
            </div>
            <div className="p-2 lg:p-3 bg-yellow-100 rounded-lg lg:rounded-xl">
              <Clock className="w-5 h-5 lg:w-6 lg:h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Interviste</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.interviews}</p>
              <p className="text-xs text-gray-500 mt-1">Programmate</p>
            </div>
            <div className="p-2 lg:p-3 bg-purple-100 rounded-lg lg:rounded-xl">
              <CalendarDays className="w-5 h-5 lg:w-6 lg:h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Azioni Rapide */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
        <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-3 lg:mb-4 flex items-center gap-2">
          <Zap className="w-4 h-4 lg:w-5 lg:h-5 text-yellow-500" />
          Azioni Rapide
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          <button
            onClick={() => navigate('/moderator/applications')}
            className="flex items-center p-3 lg:p-4 bg-blue-50 rounded-lg lg:rounded-xl hover:bg-blue-100 transition-colors group"
          >
            <div className="p-1.5 lg:p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
              <Search className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
            </div>
            <div className="ml-2 lg:ml-3 text-left">
              <p className="font-medium text-gray-900 text-sm lg:text-base">Tutte le Candidature</p>
              <p className="text-xs lg:text-sm text-gray-600">Visualizza e gestisci</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/moderator/interviews')}
            className="flex items-center p-3 lg:p-4 bg-purple-50 rounded-lg lg:rounded-xl hover:bg-purple-100 transition-colors group"
          >
            <div className="p-1.5 lg:p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
              <Calendar className="w-4 h-4 lg:w-5 lg:h-5 text-purple-600" />
            </div>
            <div className="ml-2 lg:ml-3 text-left">
              <p className="font-medium text-gray-900 text-sm lg:text-base">Gestione Interviste</p>
              <p className="text-xs lg:text-sm text-gray-600">Programma e segui</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/moderator/contacts')}
            className="flex items-center p-3 lg:p-4 bg-green-50 rounded-lg lg:rounded-xl hover:bg-green-100 transition-colors group"
          >
            <div className="p-1.5 lg:p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
              <Phone className="w-4 h-4 lg:w-5 lg:h-5 text-green-600" />
            </div>
            <div className="ml-2 lg:ml-3 text-left">
              <p className="font-medium text-gray-900 text-sm lg:text-base">I Miei Contatti</p>
              <p className="text-xs lg:text-sm text-gray-600">Storico contatti</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/moderator/interviews/new')}
            className="flex items-center p-3 lg:p-4 bg-orange-50 rounded-lg lg:rounded-xl hover:bg-orange-100 transition-colors group"
          >
            <div className="p-1.5 lg:p-2 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors">
              <Plus className="w-4 h-4 lg:w-5 lg:h-5 text-orange-600" />
            </div>
            <div className="ml-2 lg:ml-3 text-left">
              <p className="font-medium text-gray-900 text-sm lg:text-base">Nuova Intervista</p>
              <p className="text-xs lg:text-sm text-gray-600">Crea subito</p>
            </div>
          </button>
        </div>
      </div>

      {/* Ultime Candidature */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-lg lg:text-xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
            Ultime Candidature
          </h2>
          <button
            onClick={() => navigate('/moderator/applications')}
            className="flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors text-sm lg:text-base"
          >
            Vedi tutte
            <ArrowRight className="w-3 h-3 lg:w-4 lg:h-4 ml-1" />
          </button>
        </div>
        
        {/* Desktop: Tabella */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Candidato
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Azienda
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Posizione
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Stato
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {recentApplications.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium">Nessuna candidatura trovata</p>
                      <p className="text-sm">Non ci sono ancora candidature nel sistema</p>
                    </div>
                  </td>
                </tr>
              ) : (
                recentApplications.map((application) => (
                  <tr key={application.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg mr-3">
                          <UserCheck className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {application.userName || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {application.userEmail || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="p-1 bg-gray-100 rounded mr-2">
                          <Building2 className="w-3 h-3 text-gray-600" />
                        </div>
                        <div className="text-sm font-medium text-gray-900">{application.companyName}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{application.position}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {application.appliedDate ? format(application.appliedDate.toDate(), 'dd/MM/yyyy', { locale: it }) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(application.status)}
                        <span className="ml-2 text-sm font-medium text-gray-900">{application.status}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setSelectedApplication(application)}
                          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Visualizza dettagli"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedApplication(application);
                            setShowContactModal(true);
                          }}
                          className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors"
                          title="Aggiungi contatto"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: Cards */}
        <div className="lg:hidden p-3 lg:p-4 space-y-3 lg:space-y-4">
          {recentApplications.length === 0 ? (
            <div className="text-center py-6 lg:py-8">
              <div className="text-gray-500">
                <Users className="w-12 h-12 lg:w-16 lg:h-16 mx-auto mb-3 lg:mb-4 text-gray-300" />
                <p className="text-base lg:text-lg font-medium">Nessuna candidatura trovata</p>
                <p className="text-xs lg:text-sm">Non ci sono ancora candidature nel sistema</p>
              </div>
            </div>
          ) : (
            recentApplications.map((application) => (
              <div key={application.id} className="bg-gray-50 rounded-lg lg:rounded-xl p-3 lg:p-4 border border-gray-200 hover:shadow-md transition-shadow">
                {/* Header Card */}
                <div className="flex justify-between items-start mb-3 lg:mb-4">
                  <div className="flex-1">
                    <div className="flex items-center mb-1 lg:mb-2">
                      <div className="p-1 bg-blue-100 rounded mr-2">
                        <Building2 className="w-3 h-3 text-blue-600" />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-base lg:text-lg">{application.companyName}</h3>
                    </div>
                    <p className="text-xs lg:text-sm text-gray-600 mb-1">{application.position}</p>
                    <div className="flex items-center text-xs lg:text-sm text-gray-600">
                      <UserCheck className="w-3 h-3 lg:w-4 lg:h-4 mr-1" />
                      <span className="font-medium">{application.userName || 'N/A'}</span>
                    </div>
                    <p className="text-xs text-gray-500">{application.userEmail || 'N/A'}</p>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => setSelectedApplication(application)}
                      className="p-1.5 lg:p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Visualizza dettagli"
                    >
                      <Eye className="w-3 h-3 lg:w-4 lg:h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedApplication(application);
                        setShowContactModal(true);
                      }}
                      className="p-1.5 lg:p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors"
                      title="Aggiungi contatto"
                    >
                      <Plus className="w-3 h-3 lg:w-4 lg:h-4" />
                    </button>
                  </div>
                </div>

                {/* Dettagli Card */}
                <div className="space-y-1.5 lg:space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs lg:text-sm text-gray-600">Data Candidatura:</span>
                    <span className="text-xs lg:text-sm font-medium text-gray-900">
                      {application.appliedDate ? format(application.appliedDate.toDate(), 'dd/MM/yyyy', { locale: it }) : 'N/A'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs lg:text-sm text-gray-600">Stato:</span>
                    <div className="flex items-center">
                      {getStatusIcon(application.status)}
                      <span className="ml-1 text-xs lg:text-sm font-medium text-gray-900">{application.status}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs lg:text-sm text-gray-600">Metodo:</span>
                    <div className="flex items-center">
                      <MessageSquare className="w-3 h-3 lg:w-4 lg:h-4" />
                      <span className="ml-1 text-xs lg:text-sm font-medium text-gray-900 capitalize">{application.contactMethod}</span>
                    </div>
                  </div>

                  {application.notes && (
                    <div className="mt-2 lg:mt-3 pt-2 lg:pt-3 border-t border-gray-200">
                      <span className="text-xs lg:text-sm text-gray-600 block mb-1">Note:</span>
                      <p className="text-xs lg:text-sm text-gray-900 bg-white p-2 rounded-lg border">{application.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Ultimi Contatti */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-lg lg:text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-4 h-4 lg:w-5 lg:h-5 text-green-600" />
            Ultimi Contatti con Aziende
          </h2>
          <button
            onClick={() => navigate('/moderator/contacts')}
            className="flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors text-sm lg:text-base"
          >
            Vedi tutti
            <ArrowRight className="w-3 h-3 lg:w-4 lg:h-4 ml-1" />
          </button>
        </div>
        
        {/* Desktop: Tabella */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Candidato
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Azienda
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Data Contatto
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Metodo
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Risposta
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {recentContacts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium">Nessun contatto trovato</p>
                      <p className="text-sm">Non hai ancora contatti nel database</p>
                    </div>
                  </td>
                </tr>
              ) : (
                recentContacts.map((contact) => {
                  const candidateInfo = getCandidateInfo(contact);
                  return (
                    <tr key={contact.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="p-2 bg-blue-100 rounded-lg mr-3">
                            <UserCheck className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{candidateInfo.name}</div>
                            <div className="text-sm text-gray-500">{candidateInfo.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="p-1 bg-gray-100 rounded mr-2">
                            <Building2 className="w-3 h-3 text-gray-600" />
                          </div>
                          <div className="text-sm font-medium text-gray-900">{contact.companyName}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {contact.contactDate ? format(contact.contactDate.toDate(), 'dd/MM/yyyy', { locale: it }) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getContactMethodIcon(contact.contactMethod)}
                          <span className="ml-2 text-sm text-gray-900 capitalize">{contact.contactMethod}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                          contact.response === 'positive' ? 'bg-green-100 text-green-800' :
                          contact.response === 'negative' ? 'bg-red-100 text-red-800' :
                          contact.response === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {contact.response === 'positive' ? 'Positiva' :
                           contact.response === 'negative' ? 'Negativa' :
                           contact.response === 'pending' ? 'In Attesa' :
                           contact.response === 'no-response' ? 'Nessuna Risposta' : 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => openEditModal(contact)}
                          className="p-2 text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50 rounded-lg transition-colors"
                          title="Modifica contatto"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.25 2.25 0 113.182 3.182L7.5 19.213l-4.5 1.5 1.5-4.5 12.362-12.363z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: Cards */}
        <div className="lg:hidden p-3 lg:p-4 space-y-3 lg:space-y-4">
          {recentContacts.length === 0 ? (
            <div className="text-center py-6 lg:py-8">
              <div className="text-gray-500">
                <FileText className="w-12 h-12 lg:w-16 lg:h-16 mx-auto mb-3 lg:mb-4 text-gray-300" />
                <p className="text-base lg:text-lg font-medium">Nessun contatto trovato</p>
                <p className="text-xs lg:text-sm">Non hai ancora contatti nel database</p>
              </div>
            </div>
          ) : (
            recentContacts.map((contact) => {
              const candidateInfo = getCandidateInfo(contact);
              return (
                <div key={contact.id} className="bg-gray-50 rounded-lg lg:rounded-xl p-3 lg:p-4 border border-gray-200 hover:shadow-md transition-shadow">
                  {/* Header Card */}
                  <div className="flex justify-between items-start mb-3 lg:mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-1 lg:mb-2">
                        <div className="p-1 bg-gray-100 rounded mr-2">
                          <Building2 className="w-3 h-3 text-gray-600" />
                        </div>
                        <h3 className="font-semibold text-gray-900 text-base lg:text-lg">{contact.companyName}</h3>
                      </div>
                      <div className="flex items-center text-xs lg:text-sm text-gray-600 mb-1">
                        <UserCheck className="w-3 h-3 lg:w-4 lg:h-4 mr-1" />
                        <span className="font-medium">{candidateInfo.name}</span>
                      </div>
                      <p className="text-xs text-gray-500">{candidateInfo.email}</p>
                    </div>
                    <button
                      onClick={() => openEditModal(contact)}
                      className="p-1.5 lg:p-2 text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50 rounded-lg transition-colors"
                      title="Modifica contatto"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3 lg:w-4 lg:h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.25 2.25 0 113.182 3.182L7.5 19.213l-4.5 1.5 1.5-4.5 12.362-12.363z" />
                      </svg>
                    </button>
                  </div>

                  {/* Dettagli Card */}
                  <div className="space-y-1.5 lg:space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs lg:text-sm text-gray-600">Data Contatto:</span>
                      <span className="text-xs lg:text-sm font-medium text-gray-900">
                        {contact.contactDate ? format(contact.contactDate.toDate(), 'dd/MM/yyyy', { locale: it }) : 'N/A'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs lg:text-sm text-gray-600">Metodo:</span>
                      <div className="flex items-center">
                        {getContactMethodIcon(contact.contactMethod)}
                        <span className="ml-1 text-xs lg:text-sm font-medium text-gray-900 capitalize">{contact.contactMethod}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs lg:text-sm text-gray-600">Risposta:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        contact.response === 'positive' ? 'bg-green-100 text-green-800' :
                        contact.response === 'negative' ? 'bg-red-100 text-red-800' :
                        contact.response === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {contact.response === 'positive' ? 'Positiva' :
                         contact.response === 'negative' ? 'Negativa' :
                         contact.response === 'pending' ? 'In Attesa' :
                         contact.response === 'no-response' ? 'Nessuna Risposta' : 'N/A'}
                      </span>
                    </div>

                    {contact.contactPerson && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs lg:text-sm text-gray-600">Contattato:</span>
                        <span className="text-xs lg:text-sm font-medium text-gray-900">{contact.contactPerson}</span>
                      </div>
                    )}

                    {contact.followUpDate && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs lg:text-sm text-gray-600">Follow Up:</span>
                        <span className="text-xs lg:text-sm font-medium text-gray-900">
                          {format(contact.followUpDate.toDate(), 'dd/MM/yyyy', { locale: it })}
                        </span>
                      </div>
                    )}

                    {contact.notes && (
                      <div className="mt-2 lg:mt-3 pt-2 lg:pt-3 border-t border-gray-200">
                        <span className="text-xs lg:text-sm text-gray-600 block mb-1">Note:</span>
                        <p className="text-xs lg:text-sm text-gray-900 bg-white p-2 rounded-lg border">{contact.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Modal Dettagli Candidatura */}
      {selectedApplication && !showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 lg:mb-6">
              <h3 className="text-lg lg:text-xl font-bold text-gray-900">Dettagli Candidatura</h3>
              <button
                onClick={closeDetailModal}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 lg:w-6 lg:h-6" />
              </button>
            </div>
            
            <div className="space-y-4 lg:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                <div className="bg-gray-50 p-3 lg:p-4 rounded-lg lg:rounded-xl">
                  <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">Candidato</label>
                  <p className="text-xs lg:text-sm text-gray-900">{selectedApplication.userName || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 p-3 lg:p-4 rounded-lg lg:rounded-xl">
                  <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">Email</label>
                  <p className="text-xs lg:text-sm text-gray-900">{selectedApplication.userEmail || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 p-3 lg:p-4 rounded-lg lg:rounded-xl">
                  <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">Azienda</label>
                  <p className="text-xs lg:text-sm text-gray-900">{selectedApplication.companyName}</p>
                </div>
                <div className="bg-gray-50 p-3 lg:p-4 rounded-lg lg:rounded-xl">
                  <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">Posizione</label>
                  <p className="text-xs lg:text-sm text-gray-900">{selectedApplication.position}</p>
                </div>
                <div className="bg-gray-50 p-3 lg:p-4 rounded-lg lg:rounded-xl">
                  <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">Data Candidatura</label>
                  <p className="text-xs lg:text-sm text-gray-900">
                    {selectedApplication.appliedDate ? format(selectedApplication.appliedDate.toDate(), 'dd/MM/yyyy', { locale: it }) : 'N/A'}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 lg:p-4 rounded-lg lg:rounded-xl">
                  <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">Stato</label>
                  <div className="flex items-center">
                    {getStatusIcon(selectedApplication.status)}
                    <span className="ml-2 text-xs lg:text-sm text-gray-900">{selectedApplication.status}</span>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 lg:p-4 rounded-lg lg:rounded-xl">
                  <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">Metodo Contatto</label>
                  <div className="flex items-center">
                    <MessageSquare className="w-3 h-3 lg:w-4 lg:h-4" />
                    <span className="ml-2 text-xs lg:text-sm text-gray-900 capitalize">{selectedApplication.contactMethod}</span>
                  </div>
                </div>
              </div>
              
              {selectedApplication.notes && (
                <div className="bg-gray-50 p-3 lg:p-4 rounded-lg lg:rounded-xl">
                  <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">Note</label>
                  <p className="text-xs lg:text-sm text-gray-900">{selectedApplication.notes}</p>
                </div>
              )}
              
              {selectedApplication.companyDetails && (
                <div className="bg-gray-50 p-3 lg:p-4 rounded-lg lg:rounded-xl">
                  <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">Dettagli Azienda</label>
                  <div className="space-y-1 lg:space-y-2">
                    {selectedApplication.companyDetails.address && (
                      <p className="text-xs lg:text-sm text-gray-900"><strong>Indirizzo:</strong> {selectedApplication.companyDetails.address}</p>
                    )}
                    {selectedApplication.companyDetails.phone && (
                      <p className="text-xs lg:text-sm text-gray-900"><strong>Telefono:</strong> {selectedApplication.companyDetails.phone}</p>
                    )}
                    {selectedApplication.companyDetails.website && (
                      <p className="text-xs lg:text-sm text-gray-900"><strong>Sito Web:</strong> {selectedApplication.companyDetails.website}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-2 lg:space-x-3 mt-4 lg:mt-6">
              <button
                onClick={() => {
                  setShowContactModal(true);
                }}
                className="px-4 py-2 lg:px-6 lg:py-3 bg-green-600 text-white rounded-lg lg:rounded-xl hover:bg-green-700 transition-colors font-medium text-sm lg:text-base"
              >
                Aggiungi Contatto
              </button>
              <button
                onClick={closeDetailModal}
                className="px-4 py-2 lg:px-6 lg:py-3 bg-gray-300 text-gray-700 rounded-lg lg:rounded-xl hover:bg-gray-400 transition-colors font-medium text-sm lg:text-base"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Aggiungi Contatto */}
      {showContactModal && selectedApplication && (
        <AddContactModal
          isOpen={showContactModal}
          onClose={closeContactModal}
          application={selectedApplication}
          onContactAdded={loadData}
        />
      )}

      {/* Modal Modifica Contatto */}
      {showEditModal && editContact && (
        <EditContactModal
          isOpen={showEditModal}
          onClose={closeEditModal}
          contact={editContact}
          onContactUpdated={loadData}
        />
      )}
    </div>
  );
};

export default ModeratorDashboard; 